import React, { useState, useEffect } from 'react';
import { Code2, Database, Globe, Zap, CheckCircle2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import api from '../utils/api';

const ProjectPanel = ({ project }) => {
  const [activityLog, setActivityLog] = useState([]);

  useEffect(() => {
    const loadActivity = async () => {
      try {
        const response = await api.get('/activity');
        const filtered = (response.data || []).filter(a => a.projectId === project.id);
        setActivityLog(filtered);
      } catch {
        setActivityLog([]);
      }
    };
    loadActivity();
  }, [project.id]);
  const getStatusColor = (status) => {
    switch (status) {
      case 'deployed':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'building':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'testing':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'draft':
        return 'bg-slate-500/20 text-muted-foreground border-slate-500/30';
      default:
        return 'bg-slate-500/20 text-muted-foreground border-slate-500/30';
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl text-foreground">{project.name}</CardTitle>
              <Badge className={`${getStatusColor(project.status)} capitalize px-3 py-1`}>
                {project.status}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-2">{project.description}</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Credits Used', value: project.credits || 0 },
                { label: 'Technologies', value: project.technology?.length || 0 },
                { label: 'Days Ago', value: 3 },
                { label: 'Complete', value: '100%' }
              ].map((stat, i) => (
                <div key={i} className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {project.technology && project.technology.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Code2 className="w-5 h-5" />
                Technology Stack
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {project.technology.map((tech, index) => (
                  <Badge key={index} variant="outline" className="px-3 py-1 text-sm border-input text-foreground/80">
                    {tech}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-muted">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-muted-foreground">Overview</TabsTrigger>
            <TabsTrigger value="deployment" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-muted-foreground">Deployment</TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-muted-foreground">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg text-foreground">Project Features</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {['User Authentication', 'RESTful API', 'Database Integration', 'Responsive UI', 'Testing Suite', 'Deployment'].map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      <span className="text-foreground/80">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="deployment" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                  <Globe className="w-5 h-5" />
                  Deployment Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.previewUrl ? (
                  <>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Live URL</label>
                      <div className="mt-1 flex items-center gap-2">
                        <input type="text" readOnly value={project.previewUrl} className="flex-1 px-3 py-2 border border-input rounded-lg bg-background text-sm text-foreground" />
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-500">Visit</Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="w-4 h-4 text-emerald-400" />
                          <span className="text-sm font-medium text-emerald-300">Status</span>
                        </div>
                        <p className="text-lg font-bold text-emerald-400">Live</p>
                      </div>
                      <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Database className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-medium text-blue-300">Database</span>
                        </div>
                        <p className="text-lg font-bold text-blue-400">Connected</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Globe className="w-12 h-12 text-muted-foreground/70 mx-auto mb-3" />
                    <p className="text-muted-foreground">Not deployed yet</p>
                    <Button className="mt-4 bg-blue-600 hover:bg-blue-500">Deploy Now</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg text-foreground">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activityLog.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>
                  ) : (
                    activityLog.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 pb-4 border-b border-border last:border-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                        <div className="flex-1">
                          <p className="text-sm text-foreground">{log.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">{new Date(log.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3">
              <Button className="bg-blue-600 hover:bg-blue-500">Open in Editor</Button>
              <Button variant="outline" className="border-input text-foreground/80 hover:bg-secondary">Download Code</Button>
              <Button variant="outline" className="border-input text-foreground/80 hover:bg-secondary">GitHub Export</Button>
              <Button variant="outline" className="border-input text-foreground/80 hover:bg-secondary">Share</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectPanel;
